import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useAccent } from "@/context/AccentContext";
import { SectionTitle, StatusChip, Loading } from "@/components/common";
import { api } from "@/lib/api";
import { Check, Palette, Wrench, Lock, Clock, Settings2, ShieldAlert, FileText } from "lucide-react";
import { toast } from "sonner";

// Config areas with governance state. Locked business rules are NOT freely editable.
const CONFIG_AREAS = [
  { name: "User Accounts", state: "future", roles: ["owner"] },
  { name: "Roles & Permissions", state: "locked", roles: ["owner"] },
  { name: "Crews", state: "active", roles: ["owner"], note: "Manage in Crews module" },
  { name: "Pricing Configuration", state: "locked", roles: ["owner"] },
  { name: "Discount & Approval Thresholds", state: "locked", roles: ["owner"] },
  { name: "Membership Configuration", state: "locked", roles: ["owner"] },
  { name: "Daily Production Targets", state: "review", roles: ["owner"] },
  { name: "Equipment Categories", state: "active", roles: ["owner"] },
  { name: "Critical-for-Departure Configuration", state: "active", roles: ["owner", "manager"] },
  { name: "Hub Locations / Geofence", state: "future", roles: ["owner"] },
  { name: "Shadow Scan Configuration", state: "future", roles: ["owner"] },
  { name: "Photo / Documentation Requirements", state: "future", roles: ["owner"] },
  { name: "Notification Rules", state: "future", roles: ["owner", "manager"] },
  { name: "Academy Configuration", state: "future", roles: ["owner"] },
  { name: "Security / Session Settings", state: "locked", roles: ["owner"] },
  { name: "Audit Log", state: "active", roles: ["owner"], note: "Financial Analytics → Audit Log" },
  { name: "Data / Export Controls", state: "future", roles: ["owner"] },
  { name: "Integrations (Square reserved)", state: "credentials", roles: ["owner"] },
  { name: "Feature Flags", state: "future", roles: ["owner"] },
];

const STATE_BADGE = {
  active: { label: "Configurable", status: "ready" },
  view: { label: "View Only", status: "warning" },
  locked: { label: "Locked Business Rule", status: "locked" },
  review: { label: "Owner Review Required", status: "warning" },
  future: { label: "Future Configuration", status: "info" },
  credentials: { label: "Requires Credentials", status: "warning" },
};

function TierScopeEditor() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");
  const [edit, setEdit] = useState({ essential: "", refresh: "", complete: "" });

  const load = async () => {
    const { data } = await api.get("/pricing/tier-scopes");
    setData(data);
    const first = Object.keys(data.categories)[0];
    setCat(first);
    applyCat(data, first);
  };
  useEffect(() => { load(); }, []);

  const applyCat = (d, c) => setEdit({
    essential: d.categories[c].essential.included, refresh: d.categories[c].refresh.included, complete: d.categories[c].complete.included,
    essential_tv: d.categories[c].essential.trainer_validation_required, refresh_tv: d.categories[c].refresh.trainer_validation_required, complete_tv: d.categories[c].complete.trainer_validation_required,
  });

  const pick = (c) => { setCat(c); applyCat(data, c); };

  const save = async (tier) => {
    try { await api.put("/pricing/tier-scopes", { service_category: cat, tier, included: edit[tier], trainer_validation_required: !!edit[`${tier}_tv`] }); toast.success(`${cat} · ${tier} What's Included saved`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data) return null;
  return (
    <div className="sg-card p-6 mb-6" data-testid="tier-scope-editor">
      <div className="font-head text-xl font-bold mb-1 flex items-center gap-2"><FileText size={20} style={{ color: "var(--accent)" }} /> Tier "What's Included"</div>
      <p className="text-sm mb-4" style={{ color: "var(--txt2)" }}>Owner-approved customer-facing scope per Service Category + Tier. Updates appear automatically in the Estimator. Does not change locked prices.</p>
      <select className="sg-input mb-4" value={cat} onChange={(e) => pick(e.target.value)} data-testid="tier-scope-category">
        {Object.keys(data.categories).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      {["essential", "refresh", "complete"].map((tier) => (
        <div key={tier} className="mb-3">
          <label className="text-xs uppercase tracking-wider flex items-center gap-2" style={{ color: "var(--txt2)" }}>
            {tier}
            {edit[`${tier}_tv`] && <span className="chip" style={{ background: "rgba(245,158,11,0.15)", color: "#F59E0B", borderColor: "#F59E0B", fontSize: 10 }}>TRAINER VALIDATION REQUIRED</span>}
          </label>
          <textarea className="sg-input mt-1" rows={2} value={edit[tier]} onChange={(e) => setEdit((s) => ({ ...s, [tier]: e.target.value }))} data-testid={`tier-scope-${tier}`} />
          <div className="flex items-center gap-3 mt-1">
            <label className="flex items-center gap-2 text-xs" style={{ color: "var(--txt2)" }}>
              <input type="checkbox" checked={!!edit[`${tier}_tv`]} onChange={(e) => setEdit((s) => ({ ...s, [`${tier}_tv`]: e.target.checked }))} data-testid={`tier-scope-tv-${tier}`} /> Trainer validation required
            </label>
            <button className="sg-btn" style={{ minHeight: 36 }} onClick={() => save(tier)} data-testid={`tier-scope-save-${tier}`}>Save {tier}</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdvancedSettings() {
  const { user } = useAuth();
  const { key, setKey, accents } = useAccent();
  const [equipment, setEquipment] = useState(null);
  const isOwner = user.role === "owner";

  const load = async () => { const { data } = await api.get("/equipment"); setEquipment(data); };
  useEffect(() => { load(); }, []);

  const toggleCritical = async (item) => {
    if (!isOwner) { toast.error("Only the Owner can change critical classification"); return; }
    try {
      const { data } = await api.patch(`/equipment/${item.id}/critical`, { critical: !item.critical });
      setEquipment((eq) => eq.map((e) => (e.id === item.id ? { ...e, critical: data.critical } : e)));
      toast.success(`${item.name} · Critical for Departure = ${data.critical ? "Yes" : "No"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const visibleAreas = CONFIG_AREAS.map((a) => {
    if (a.roles.includes(user.role)) {
      // For managers, critical config is view-only (owner configures the toggles).
      if (user.role === "manager" && a.name === "Critical-for-Departure Configuration") return { ...a, state: "view" };
      return a;
    }
    // Owner-only areas are shown to managers as read-only Locked Business Rule rows.
    if (user.role === "manager") return { ...a, state: "locked", note: "Owner-only" };
    return null;
  }).filter(Boolean);

  return (
    <div className="max-w-5xl">
      <SectionTitle sub={isOwner ? "Owner-level system configuration" : "Operational configuration (manager subset)"}>Advanced Settings</SectionTitle>

      {/* Critical-for-Departure configuration */}
      <div className="sg-card p-6 mb-6" data-testid="critical-config">
        <div className="font-head text-xl font-bold mb-1 flex items-center gap-2"><Wrench size={20} style={{ color: "var(--accent)" }} /> Critical for Departure</div>
        <p className="text-sm mb-4" style={{ color: "var(--txt2)" }}>
          The HUB001 departure engine blocks on any Critical asset that is Missing / Damaged / Maintenance Required.
          {isOwner ? " Toggle which assets are critical." : " View only — only the Owner can change critical classification."}
        </p>
        {!isOwner && <div className="mb-3"><StatusChip status="warning" label="View Only (Owner configures)" /></div>}
        {!equipment ? <Loading /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {equipment.map((i) => (
              <div key={i.id} className="sg-card-elev p-4 flex items-center justify-between" data-testid={`crit-${i.id}`}>
                <div>
                  <div className="font-semibold text-sm">{i.name}</div>
                  {i.specs && <div className="text-xs" style={{ color: "var(--txt2)" }}>{i.specs}</div>}
                  {i.certification_required && <div className="mt-1"><StatusChip status="warning" label="Cert Required" /></div>}
                </div>
                <button onClick={() => toggleCritical(i)} disabled={!isOwner} data-testid={`crit-toggle-${i.id}`}
                  aria-checked={!!i.critical} data-critical={i.critical ? "true" : "false"} role="switch"
                  className="w-14 h-8 rounded-full p-1 transition-colors" style={{ background: i.critical ? "var(--accent)" : "var(--brd)", opacity: isOwner ? 1 : 0.6 }}>
                  <div className="w-6 h-6 rounded-full bg-white transition-transform" style={{ transform: i.critical ? "translateX(24px)" : "none" }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Signature accent (owner) */}
      {isOwner && <TierScopeEditor />}
      {isOwner && (
        <div className="sg-card p-6 mb-6" data-testid="accent-picker">
          <div className="font-head text-xl font-bold mb-1 flex items-center gap-2"><Palette size={20} style={{ color: "var(--accent)" }} /> Signature Accent</div>
          <p className="text-sm mb-4" style={{ color: "var(--txt2)" }}>Carries across UI, gear, and branding.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Object.entries(accents).map(([k, a]) => (
              <button key={k} onClick={() => { setKey(k); toast.success(`${a.name} locked`); }} data-testid={`accent-${k}`}
                className={`sg-card-elev p-5 text-left ${key === k ? "tile-active" : ""}`}>
                <div className="w-full h-12 rounded-lg mb-3" style={{ background: a.hex }} />
                <div className="flex items-center justify-between"><span className="font-head font-bold text-sm">{a.name}</span>{key === k && <Check size={16} style={{ color: a.hex }} />}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Governance grid */}
      <div className="sg-card p-6" data-testid="config-areas">
        <div className="font-head text-xl font-bold mb-1 flex items-center gap-2"><Settings2 size={20} style={{ color: "var(--accent)" }} /> Configuration Areas</div>
        <p className="text-sm mb-4" style={{ color: "var(--txt2)" }}>Locked business rules require Owner approval and cannot be silently modified by any user or AI.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {visibleAreas.map((a) => {
            const b = STATE_BADGE[a.state];
            return (
              <div key={a.name} className="sg-card-elev p-3 flex items-center justify-between" data-testid={`area-${a.name.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                <div className="flex items-center gap-2">
                  {a.state === "locked" && <Lock size={14} style={{ color: "var(--danger)" }} />}
                  {a.state === "review" && <ShieldAlert size={14} style={{ color: "var(--warn)" }} />}
                  {a.state === "future" && <Clock size={14} style={{ color: "var(--info)" }} />}
                  <div>
                    <div className="text-sm font-semibold">{a.name}</div>
                    {a.note && <div className="text-xs" style={{ color: "var(--txt2)" }}>{a.note}</div>}
                  </div>
                </div>
                <StatusChip status={b.status} label={b.label} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
